// Shared rich-text editor for every main text-entry area in
// Marketing+ (HQ Template mode AND franchisee campaign mode):
//   • Intro Text
//   • Section Header  (via ``singleLine`` prop — Enter is suppressed)
//   • Section Text
//
// This component intentionally has NO toolbar of its own — every
// formatting control (font family / size / colour / bold / italic /
// underline / align / link / insert name / reset) lives in the shared
// ``<HeadingStyleControls>`` row rendered immediately below by the
// parent. Callers pass a ``ref`` and use its imperative methods
// (``insertLink``, ``insertPlaceholder``, ``focus``) to wire those
// controls back to this editor's selection.
//
// The backend sanitiser (``_sanitise_intro_html``) keeps only ``<b>
// <strong><i><em><u><br><div><p><span><font><a>`` + ``style="text-
// align:…"``, ``style="color:…"``, ``style="font-family:…"`` and
// validated ``href``, so nothing dangerous can slip into the
// recipient's inbox.
import {
  forwardRef, useEffect, useImperativeHandle, useRef,
} from "react";
import DOMPurify from "dompurify";

// Single source of truth for the shared Marketing+ font whitelist —
// mirrored server-side in ``_SAFE_HEADING_FONTS`` (portal_marketing_
// routes.py). Every font-family control in the composer imports this
// list; there is no second list anywhere in the codebase.
export const HEADING_FONTS = [
  { name: "Arial", stack: "Arial, Helvetica, sans-serif" },
  { name: "Helvetica", stack: "Helvetica, Arial, sans-serif" },
  { name: "Verdana", stack: "Verdana, Geneva, sans-serif" },
  { name: "Tahoma", stack: "Tahoma, Verdana, sans-serif" },
  { name: "Trebuchet MS", stack: "'Trebuchet MS', Helvetica, sans-serif" },
  { name: "Georgia", stack: "Georgia, 'Times New Roman', serif" },
  { name: "Times New Roman", stack: "'Times New Roman', Times, serif" },
  { name: "Courier New", stack: "'Courier New', Courier, monospace" },
  // Script / handwritten — no web-font loading, locally-installed
  // only. Cursive fallback keeps the flavour on any device.
  { name: "Segoe Script", stack: "'Segoe Script', 'Lucida Handwriting', cursive" },
  { name: "Brush Script MT", stack: "'Brush Script MT', 'Segoe Script', cursive" },
  { name: "Lucida Handwriting", stack: "'Lucida Handwriting', cursive" },
];

// DOMPurify config — mirrors the backend sanitiser's allowlist so
// paste operations don't briefly show forbidden markup before the
// server strips it on save.
const SANITIZE_CFG = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "br", "div", "p", "span", "font", "a"],
  ALLOWED_ATTR: ["style", "color", "href", "target", "rel", "face"],
  ALLOWED_CSS_PROPERTIES: ["text-align", "color", "font-family"],
};

// Convert a HeadingStyleControls-style dict ``{font_family, font_size,
// font_weight, color, bold, italic, underline, align}`` into a React
// inline-style object. Mirrors what the backend renderer emits so the
// contentEditable visually matches the Live Preview iframe — a user
// bolding + reddening intro text sees the change in the editor
// itself, not just in the preview panel.
function styleDictToCss(style) {
  if (!style || typeof style !== "object") return null;
  const stack = (HEADING_FONTS.find((f) => f.name === style.font_family) || {}).stack;
  const boldOn = style.bold === true;
  const weight = boldOn ? 700 : (typeof style.font_weight === "number" ? style.font_weight : 400);
  const out = {};
  if (stack) out.fontFamily = stack;
  if (typeof style.font_size === "number") out.fontSize = `${style.font_size}px`;
  out.fontWeight = weight;
  if (style.color) out.color = style.color;
  if (style.italic) out.fontStyle = "italic";
  if (style.underline) out.textDecoration = "underline";
  if (style.align && ["left", "center", "right"].includes(style.align)) {
    out.textAlign = style.align;
  }
  return out;
}

const MarketingIntroEditor = forwardRef(function MarketingIntroEditor({
  value, onChange, placeholder, testid,
  // ``singleLine`` renders the editor styled for a heading and
  // suppresses Enter so the section-header row can never grow into
  // a multi-line block. Used by Section Header.
  singleLine = false,
  // Optional style dict driving the contentEditable's inline
  // typography — SAME shape used by the single-toolbar
  // HeadingStyleControls row. When provided, the user sees the
  // active family/size/colour/B/I/U/align applied to the editable
  // text as they type, matching the Live Preview visually.
  blockStyle = null,
}, apiRef) {
  const ref = useRef(null);

  // Only sync the incoming `value` into the DOM when it actually
  // differs — otherwise React would clobber the caret on every keystroke.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const clean = value ? DOMPurify.sanitize(value, SANITIZE_CFG) : "";
    if (el.innerHTML !== clean) {
      el.innerHTML = clean;
    }
  }, [value]);

  // Expose imperative methods so the shared HeadingStyleControls
  // toolbar rendered by the parent can drive the editor's selection.
  useImperativeHandle(apiRef, () => ({
    focus() {
      if (ref.current) ref.current.focus();
    },
    insertPlaceholder() {
      if (!ref.current) return;
      ref.current.focus();
      document.execCommand("insertHTML", false, "{{first_name}}");
      onChange(ref.current.innerHTML);
    },
    insertLink() {
      if (!ref.current) return;
      ref.current.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.toString().length === 0) {
        window.alert("Select the text you want to turn into a link first.");
        return;
      }
      const url = window.prompt("Link URL", "https://");
      if (!url) return;
      const trimmed = url.trim();
      // Only allow http(s):// and mailto: — anything else is rejected
      // client-side (defence-in-depth; server sanitiser is the source
      // of truth).
      if (!/^(https?:\/\/|mailto:)/i.test(trimmed)) {
        window.alert("Please enter a link starting with http://, https:// or mailto:");
        return;
      }
      document.execCommand("createLink", false, trimmed);
      onChange(ref.current.innerHTML);
    },
  }), [onChange]);

  return (
    <div
      className="border border-stone-300 rounded-xl overflow-hidden focus-within:border-stone-950 bg-white"
      data-testid={testid}
    >
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onKeyDown={(e) => {
          // Single-line mode (Section Header): Enter should NOT create
          // a new paragraph — the section-header row is a headline,
          // not a multi-line block. Shift+Enter still allowed as an
          // explicit <br/> so the author can hard-wrap if desired.
          if (singleLine && e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
          }
        }}
        onPaste={(e) => {
          // In single-line mode, coerce pasted content to plain text so
          // a wall of formatted paragraphs can't invade a heading.
          if (!singleLine) return;
          e.preventDefault();
          const text = (e.clipboardData || window.clipboardData).getData("text/plain") || "";
          const flat = text.replace(/[\r\n]+/g, " ");
          document.execCommand("insertText", false, flat);
        }}
        data-placeholder={placeholder || "Type here…"}
        style={styleDictToCss(blockStyle) || undefined}
        className={
          singleLine
            ? "min-h-[44px] px-3 py-2 text-sm leading-tight focus:outline-none cm-rte whitespace-nowrap overflow-x-auto"
            : "min-h-[120px] p-3 text-sm leading-relaxed focus:outline-none cm-rte"
        }
        data-testid={`${testid}-area`}
      />
      <style>{`.cm-rte:empty::before{content:attr(data-placeholder);color:#a8a29e;} .cm-rte a{color:#1a56db;text-decoration:underline;}`}</style>
    </div>
  );
});

export default MarketingIntroEditor;
